/**
 * Guardas de rota.
 *
 * **Estas guardas são conveniência de navegação, não segurança.** O legado tratava o
 * redirecionamento no cliente como controle de acesso (achado 4), o que é contornável desabilitando
 * JavaScript. A decisão que vale é a do servidor, em cada requisição.
 *
 * O papel delas aqui é evitar que o usuário veja uma tela que não vai funcionar.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import type { Acao } from '@emaster/dominio';

import { useAutenticacao } from './contexto.tsx';

function Carregando() {
  return (
    <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-3 border-gray-200 border-t-brand-accent" />
        <p className="text-sm text-gray-500">Verificando sua sessão...</p>
      </div>
    </div>
  );
}

/** Tela para usuário confirmado que ainda não recebeu papel. */
function AguardandoLiberacao() {
  const { sessao, sair } = useAutenticacao();

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="max-w-md rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
        <h1 className="mb-3 text-xl font-bold text-brand-blue">Acesso aguardando liberação</h1>
        <p className="mb-6 text-sm leading-relaxed text-gray-600">
          Sua conta <strong className="text-brand-blue">{sessao?.email}</strong> foi confirmada, mas
          ainda não tem um perfil de acesso atribuído. Um administrador precisa liberar o acesso
          antes do primeiro uso.
        </p>
        <button
          type="button"
          onClick={() => void sair()}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200"
        >
          Sair
        </button>
      </div>
    </div>
  );
}

/**
 * Exige sessão ativa.
 *
 * Preserva o destino em `state`, para devolver o usuário à página pretendida depois do login.
 */
export function ExigeAutenticacao({ children }: { readonly children: ReactNode }) {
  const { estado, aguardandoLiberacao } = useAutenticacao();
  const local = useLocation();

  if (estado === 'carregando') return <Carregando />;
  if (estado === 'anonimo') return <Navigate to="/entrar" replace state={{ de: local.pathname }} />;
  if (aguardandoLiberacao) return <AguardandoLiberacao />;

  return <>{children}</>;
}

/** Exige uma permissão específica, além da sessão. */
export function ExigePermissao({
  acao,
  children,
}: {
  readonly acao: Acao;
  readonly children: ReactNode;
}) {
  const { estado, pode } = useAutenticacao();

  if (estado === 'carregando') return <Carregando />;
  if (!pode(acao)) {
    return (
      <div className="mx-auto max-w-md px-6 py-20 text-center">
        <h1 className="mb-3 text-xl font-bold text-brand-blue">Sem permissão</h1>
        <p className="text-sm leading-relaxed text-gray-600">
          Seu perfil de acesso não permite esta operação. Se você precisa dela, procure um
          administrador.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

/** Redireciona quem já está autenticado, para não ver a tela de login de novo. */
export function ApenasAnonimo({ children }: { readonly children: ReactNode }) {
  const { estado } = useAutenticacao();

  if (estado === 'carregando') return <Carregando />;
  if (estado === 'autenticado') return <Navigate to="/" replace />;

  return <>{children}</>;
}

/** Renderiza os filhos apenas quando a permissão existe. Para esconder botão e item de menu. */
export function Se({ acao, children }: { readonly acao: Acao; readonly children: ReactNode }) {
  const { pode } = useAutenticacao();
  return pode(acao) ? <>{children}</> : null;
}
